// BUTTON-9: what "a field differs from what was loaded" means, in one place.
// card_dirty_controller fingerprints a card with it; the modals whose form
// arrives later (frame-modal, app-setting-modal) take the same fingerprint on
// first load and compare against it.

// The fields a fingerprint covers. Hidden fields are kept (the TipTap bio
// editor stores its value in one; CSRF / _method add a constant). Auto-saving
// widgets wrapped in [data-card-dirty-ignore] are left out, so flipping them
// never enables Save.
//
// Select2's own controls are left out too (#18256): a multiple Select2 inserts
// its `textarea.select2-search__field` into the form when it initialises —
// often AFTER the baseline was taken — so counting it made every later
// fingerprint one field longer than the baseline and the card read dirty
// forever. It is Select2's typing box, never form data: the pick lands on the
// underlying <select>, which is still fingerprinted.
const SELECT2_INTERNALS = ".select2-container"

export function fingerprintFields(root) {
  if (!root) return []
  return Array.from(root.querySelectorAll("input, select, textarea"))
    .filter((el) => el.type !== "submit" && el.type !== "button")
    .filter((el) => !el.closest("[data-card-dirty-ignore]"))
    .filter((el) => !el.closest(SELECT2_INTERNALS))
}

// The markup a rich-text editor writes when it holds nothing: TipTap's empty
// document is `<p></p>`, and an emptied editor can leave `<p><br></p>` or a
// run of blank paragraphs / `&nbsp;`. #18260: a blank signature loads as ""
// but the emptied editor writes `<p></p>`, so Save stayed on after the text
// was typed and deleted again.
const EMPTY_EDITOR_MARKUP = /^(?:\s|&nbsp;|<br\s*\/?>|<(p|div)(?:\s[^>]*)?>(?:\s|&nbsp;|<br\s*\/?>)*<\/\1>)*$/i

// An empty editor's markup reads as "" so it compares equal to a blank field.
// Only markup is normalised: a plain field holding just spaces keeps its value.
export function normalizeEditorValue(value) {
  return value.includes("<") && EMPTY_EDITOR_MARKUP.test(value) ? "" : value
}

// One field's token. File inputs contribute the chosen filename, checkboxes and
// radios their checked state, a <select multiple> every selected option (its
// `value` reports only the first). Any other value has empty rich-text editor
// markup normalised to "" (normalizeEditorValue).
export function fieldToken(el) {
  if (el.type === "file") return `${el.name}:${el.value}`
  if (el.type === "checkbox" || el.type === "radio") return `${el.name}:${el.checked}`
  if (el.type === "select-multiple") {
    return `${el.name}:${Array.from(el.selectedOptions).map((o) => o.value).join(",")}`
  }
  return `${el.name}:${normalizeEditorValue(el.value)}`
}

// A stable string fingerprint of every field's value under `root`.
export function formFingerprint(root) {
  return fingerprintFields(root).map(fieldToken).join("|")
}

// BUTTON-9's reason on a clean Save (shared/_card_form_actions stamps the same).
export const SAVE_UNCHANGED_REASON = "Change a field first"
